import moment from "moment";
import { DatePicker, Field } from "../../ui";
import "./styles.scss";

export type CategorySchedule = {
  startDate?: string | null; // ISO
  endDate?: string | null;   // ISO
};

type Props = {
  tournamentStartDate: string; // ISO or YYYY-MM-DD
  tournamentEndDate: string;   // ISO or YYYY-MM-DD
  value: CategorySchedule;
  onChange: (next: CategorySchedule) => void;
};

const composeOnDate = (dateBase: string, time: Date | null): string | null => {
  if (!time) return null;
  const d = moment(dateBase).startOf("day");
  d.hour(time.getHours()).minute(time.getMinutes()).second(0);
  return d.format("YYYY-MM-DDTHH:mm:ss");
};

const CategoryScheduleField = ({
  tournamentStartDate,
  tournamentEndDate,
  value,
  onChange,
}: Props) => {
  const isSingleDay = moment(tournamentStartDate).isSame(tournamentEndDate, "day");
  const tStart = new Date(tournamentStartDate);
  const tEnd = new Date(tournamentEndDate);

  if (isSingleDay) {
    return (
      <div className="category-schedule-field single-day">
        <Field label="Start time" className="field-container">
          {(id) => (
            <DatePicker
              id={id}
              selected={value.startDate ? new Date(value.startDate) : null}
              onChange={(d) =>
                onChange({
                  ...value,
                  startDate: composeOnDate(tournamentStartDate, d),
                })
              }
              mode="time"
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="HH:mm"
              placeholderText="Pick a time"
            />
          )}
        </Field>
        <Field label="End time" className="field-container">
          {(id) => (
            <DatePicker
              id={id}
              selected={value.endDate ? new Date(value.endDate) : null}
              onChange={(d) =>
                onChange({
                  ...value,
                  endDate: composeOnDate(tournamentStartDate, d),
                })
              }
              mode="time"
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="HH:mm"
              placeholderText="Pick a time"
            />
          )}
        </Field>
      </div>
    );
  }

  return (
    <div className="category-schedule-field multi-day">
      <Field label="Start date &amp; time" className="field-container">
        {(id) => (
          <DatePicker
            id={id}
            selected={value.startDate ? new Date(value.startDate) : null}
            onChange={(d) =>
              onChange({
                ...value,
                startDate: d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : null,
                // when the new start is after the existing end, clear end
                endDate:
                  value.endDate && d && new Date(value.endDate) < d ? null : value.endDate,
              })
            }
            mode="datetime"
            timeIntervals={15}
            dateFormat="dd/MM/yyyy HH:mm"
            minDate={tStart}
            maxDate={tEnd}
            placeholderText="Pick a date & time"
          />
        )}
      </Field>
      <Field label="End date &amp; time" className="field-container">
        {(id) => (
          <DatePicker
            id={id}
            selected={value.endDate ? new Date(value.endDate) : null}
            onChange={(d) =>
              onChange({
                ...value,
                endDate: d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : null,
              })
            }
            mode="datetime"
            timeIntervals={15}
            dateFormat="dd/MM/yyyy HH:mm"
            minDate={value.startDate ? new Date(value.startDate) : tStart}
            maxDate={tEnd}
            placeholderText="Pick a date & time"
          />
        )}
      </Field>
    </div>
  );
};

export default CategoryScheduleField;
